package dev.lingvo.site.api

import kotlinx.browser.window

object ApiConfig {
    val baseUrl: String
        get() {
            val location = window.location
            return resolveBaseUrl(
                hostname = location.hostname,
                protocol = location.protocol,
                host = location.host,
                override = devOverride(),
            )
        }

    /**
     * Optional local override, e.g. `localStorage['lingvo:apiUrl'] = 'http://localhost:9000/api/v1'`.
     *
     * Development only. The port is easy to lose to another project's dev server, and without this
     * the only way to repoint the frontend was editing a constant and rebuilding the bundle.
     */
    private fun devOverride(): String? = runCatching {
        window.localStorage.getItem(API_URL_OVERRIDE_KEY)?.trim()?.takeIf(String::isNotEmpty)
    }.getOrNull()

    private const val API_URL_OVERRIDE_KEY = "lingvo:apiUrl"
}

/**
 * Where the browser should send API calls, given the page it is on.
 *
 * Three cases, and the middle one is the reason this exists. Deriving the API from the page's own
 * origin is right for a self-hosted deployment that puts both behind one host, and wrong for the
 * hosted product: `lingvo.dev` is a static bundle on Cloudflare Pages, and the API lives on
 * `api.lingvo.dev`. Same-origin derivation would have sent every call to a host that serves no API
 * and answers 404 — a failure that only appears once deployed.
 *
 * Extracted from [ApiConfig] so it can be tested without a browser.
 *
 * Public because that test — `jsTest/ApiConfigTest.kt` — stays in `:site` while this file moves to
 * `:kit`, and `internal` is Gradle-module-scoped. It is the only test of the one decision here that
 * is not derivable from the page's own origin.
 */
fun resolveBaseUrl(
    hostname: String,
    protocol: String,
    host: String,
    override: String? = null,
): String {
    if (hostname in LOCAL_HOSTNAMES) return override ?: LOCAL_API
    if (hostname == STAGING_PAGES_HOST || hostname.endsWith(".$STAGING_PAGES_HOST")) return STAGING_API
    if (hostname == HOSTED_ZONE || hostname.endsWith(".$HOSTED_ZONE")) return HOSTED_API
    if (hostname in HOSTED_PREVIEW_HOSTNAMES) return HOSTED_API
    // Anything else is a self-hosted deployment. Assume the API is reachable at the same origin,
    // which is what a single reverse proxy in front of both produces.
    return "$protocol//$host/api/v1"
}

private val LOCAL_HOSTNAMES = setOf("localhost", "127.0.0.1", "::1", "[::1]")

/**
 * The hosted zone, matched as a zone rather than enumerated so every owned hostname follows it.
 *
 * The leading dot is load-bearing: `endsWith("lingvo.dev")` also matches
 * `lingvo.dev.attacker.example`, which anybody can create.
 */
private const val HOSTED_ZONE = "lingvo.dev"

/**
 * The Pages preview hostnames for the projects we own.
 *
 * This is deliberately not an `endsWith(".pages.dev")` rule: anyone can deploy a fork there, and
 * that fork's sign-in page must not send its visitors' credentials to our API.
 */
private val HOSTED_PREVIEW_HOSTNAMES = setOf("lingvo-7ll.pages.dev", "lingvo-ops.pages.dev")

// 8091 rather than 8081: 8081 collides with common dev servers and the failure is silent —
// requests reach the other server and answer 404.
private const val LOCAL_API = "http://localhost:8091/api/v1"
private const val HOSTED_API = "https://api.lingvo.dev/api/v1"
private const val STAGING_PAGES_HOST = "lingvo-staging.pages.dev"
private const val STAGING_API = "https://lingvo-api-staging.fly.dev/api/v1"
